import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "@/services/axios";
import { API } from "@/config/api";
import { toast } from "sonner";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("access_token"));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(API.auth.me);
      setUser(response.data);

      // After fetching user, load their cart
      await loadUserCart();
    } catch (error) {
      console.error("Failed to fetch user:", error);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserCart = async () => {
    try {
      console.log("Loading user cart from server...");
      const response = await axios.get(API.auth.cart.get);
      console.log("Server cart response:", response.data);

      if (response.data.items && response.data.items.length > 0) {
        console.log("Found items in server cart:", response.data.items);
        localStorage.setItem("cart", JSON.stringify(response.data.items));
      } else {
        console.log("No items in server cart");
        // Don't clear localStorage if there are guest items
        const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
        if (guestCart.length === 0) {
          localStorage.removeItem("cart");
        }
      }

      // Trigger storage event for other components
      window.dispatchEvent(new Event("storage"));
    } catch (error) {
      console.error("Failed to load user cart:", error);
    }
  };

  const migrateCart = async () => {
    const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
    console.log("Guest cart before migration:", guestCart);

    // Get token directly from localStorage to ensure it's available
    const currentToken = localStorage.getItem("access_token");
    console.log("Current token:", !!currentToken);

    if (guestCart.length > 0 && currentToken) {
      try {
        console.log("Migrating guest cart to server...");
        const response = await axios.post(
          API.auth.cart.migrate,
          {
            items: guestCart,
          },
          {
            headers: { Authorization: `Bearer ${currentToken}` },
          },
        );
        console.log("Migration response:", response.data);

        if (response.data.items) {
          console.log(
            "Saving merged cart to localStorage:",
            response.data.items,
          );
          localStorage.setItem("cart", JSON.stringify(response.data.items));
          window.dispatchEvent(new Event("storage"));
        }

        return response.data;
      } catch (error) {
        console.error("Failed to migrate cart:", error);
        await loadUserCart();
        return null;
      }
    } else if (guestCart.length > 0 && !currentToken) {
      console.log("⚠️ Guest cart exists but no token yet, waiting...");
      // Wait a bit and try again
      await new Promise((resolve) => setTimeout(resolve, 500));
      return migrateCart(); // Retry
    } else {
      console.log("No guest cart to migrate, loading server cart...");
      await loadUserCart();
      return null;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(API.auth.login, { email, password });
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      setToken(access_token);
      setUser(user);

      // Important: Wait for token to be set in localStorage
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Migrate cart after successful login
      await migrateCart();

      toast.success("Logged in successfully");
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((e) => `${e.loc?.at(-1) ?? "field"}: ${e.msg}`).join(", ") : detail || "Login failed");
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(API.auth.register, userData);
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      setToken(access_token);
      setUser(user);

      // Wait a tiny bit for token to be set
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Migrate cart after successful registration
      const migratedCart = await migrateCart();

      // If migration was successful and returned items, update localStorage
      if (migratedCart?.items) {
        localStorage.setItem("cart", JSON.stringify(migratedCart.items));
        window.dispatchEvent(new Event("storage"));
      }

      // Wait a bit longer to ensure cart is saved to server
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success("Registration successful");
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((e) => `${e.loc?.at(-1) ?? "field"}: ${e.msg}`).join(", ") : detail || "Registration failed");
      return false;
    }
  };

  const logout = () => {
    // Clear cart from localStorage (privacy)
    localStorage.removeItem("cart");

    // Clear auth tokens
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    // Clear state
    setToken(null);
    setUser(null);

    // Trigger storage event for other tabs/components
    window.dispatchEvent(new Event("storage"));

    toast.success("Logged out");
  };

  // True only when the user is verified AND admin-approved (or is admin)
  const hasFullAccess = !!user && (
    user.is_admin ||
    (user.is_verified && user.account_status === "approved")
  );

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    hasFullAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
